import ApiService from '@/common/api.service'

import * as utils from '@/common/utils'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    archivedUnit: {},
    imei: null,
    serial_no: null,
    stock_no: null,
    name: null,
    partnerId: null,
    agencyId: null,
    clientId: null,
    canProceed: false,
    note: ''
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    context.commit('setIsLoading', true)

    return ApiService
      .get(`units/archives/${id}/unarchive`)
      .then((response) => {
        context.commit('setIsLoading', false)

        if (response.data.data.hasOwnProperty('id')) {
          context.commit('set', response.data.data)
          context.commit('setIsModified', false)
          context.commit('setCanProceed', true)
        }

        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @param id
   * @return Promise
   */
  post (context, { params }) {
    const unit = context.getters['get']

    let data = {
      imei: unit.imei,
      serial_no: unit.serial_no,
      stock_no: unit.stock_no,
      name: unit.name,
      note: unit.note.trim() === '' ? null : unit.note.trim(),
      partner_id: unit.partnerId,
      agency_id: unit.agencyId,
      client_id: unit.clientId
    }

    return ApiService
      .post(`units/archives/${params.id}/unarchive`, data)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`units/archive`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data.archivedUnit = Object.assign({}, data)
    state.data.imei = data.imei
    state.data.serial_no = data.serial_no
    state.data.stock_no = data.stock_no
    state.data.name = data.name
    state.data.partnerId = data.partner_id
    state.data.agencyId = data.agency_id
    state.data.clientId = data.client_id
  },

  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  setAssignments (state, data) {
    state.data.partnerId = data.partner.id
    if (data.agency) {
      state.data.agencyId = data.agency.id
    }

    if (data.client) {
      state.data.clientId = data.client.id
    }
  },

  setFormData (state, data) {
    state.form = {
      partners: data.partners
    }
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      archivedUnit: data.archivedUnit,
      imei: data.imei,
      serial_no: data.serial_no,
      stock_no: data.stock_no,
      name: data.name,
      canProceed: false,
      note: ''
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Sets canProceed in state.
   *
   * @param state    Object
   * @param proceed  Boolean
   */
  setCanProceed (state, proceed) {
    state.data.canProceed = proceed
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = Object.assign({}, defaults())
    state.isModified = false
    state.isLoading = false
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/units/archives/unitArchivesModule.js